/* eslint-disable react-refresh/only-export-components */
// src/lib/supabase/AuthGate.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getSupabaseClient, hasAuthAPI } from './safeClient';
import { identify } from '../analytics';
import { trackV2, V2Events } from '../../v2/v2Analytics';

const supabase = getSupabaseClient();

// Report a completed sign-in exactly once per browser session.
//
// This is the app's single global auth listener, which is why the event lives
// here. Until now nothing marked a successful authentication anywhere: useAuth
// called track(SIGN_IN_COMPLETE), but `track` has been a no-op since PostHog was
// removed, and the only remaining trace was a page_view on /auth/callback —
// which also fires when the provider hands back an error, so a cancelled
// LinkedIn sign-in and a successful one looked identical.
//
// Supabase emits SIGNED_IN on session restore and after a token refresh as well
// as on a real sign-in, so a bare listener would report an authentication every
// time a returning visitor opened a tab. The dedupe key is the user id in
// sessionStorage: one report per user per browser session, which is the shape of
// the question the funnel is asking ("did this visit produce a sign-in?").
const SIGNIN_KEY = 'fp_signin_reported';

function reportSignIn(session) {
  const user = session?.user;
  if (!user?.id) return;
  try {
    if (sessionStorage.getItem(SIGNIN_KEY) === user.id) return;
    sessionStorage.setItem(SIGNIN_KEY, user.id);
  } catch {
    // Storage denied: report once rather than never. An over-count in a private
    // window is a smaller error than a silently missing funnel stage.
  }
  let isNew = false;
  try {
    // Same 60-second window useAuth has always used to call an account new.
    isNew = Date.now() - new Date(user.created_at).getTime() < 60_000;
  } catch { /* unparseable timestamp — reported as a returning sign-in */ }
  try {
    trackV2(V2Events.AUTH_COMPLETED, {
      provider: user.app_metadata?.provider || 'email',
      is_new: isNew,
    });
  } catch { /* analytics must never affect the session */ }
}

const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  supabase,
});

/**
 * Provides Supabase auth state to the app using the singleton client from safeClient.js.
 * Gracefully handles missing env or misconfigured client.
 */
export function AuthGate({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasAuthAPI(supabase)) {
      console.warn('[AuthGate] Supabase client missing or misconfigured; rendering unauthenticated.');
      setLoading(false);
      return;
    }

    let mounted = true;

    // Prime session
    supabase.auth.getSession()
      .then(({ data }) => {
        if (!mounted) return;
        const s = data?.session ?? null;
        setSession(s);
        setUser(s?.user ?? null);
      })
      .catch((err) => {
        console.warn('[AuthGate] getSession error', err);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    // Subscribe to auth changes
    const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!mounted) return;
      setSession(newSession ?? null);
      setUser(newSession?.user ?? null);
      if (_event === 'SIGNED_IN') reportSignIn(newSession);
    });

    const unsubscribe = () => data?.subscription?.unsubscribe?.();
    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, []);

  // Identify user in analytics when auth state changes
  useEffect(() => {
    if (user?.id) {
      // Hash email domain for privacy (no raw email)
      const emailDomain = user.email?.split('@')[1] || 'unknown'
      identify(user.id, {
        email_domain: emailDomain,
        created_at: user.created_at,
      })
    } else {
      // User logged out - reset analytics identity
      identify(null)
    }
  }, [user])

  const value = useMemo(
    () => ({ user, session, loading, supabase }),
    [user, session, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export default AuthGate;
